import React, { useState } from "react"
import Layout from "../../components/Layout"
import { graphql } from "gatsby"
import Image from "gatsby-image"
import SEO from "../../components/SEO"
import DatePicker, { registerLocale } from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

const hr = {
  code: "hr",
  localize: {
    month: n => [
      "Siječanj", "Veljača", "Ožujak", "Travanj", "Svibanj", "Lipanj",
      "Srpanj", "Kolovoz", "Rujan", "Listopad", "Studeni", "Prosinac",
    ][n],
    day: (n, { width } = {}) => {
      const wide = [
        "Nedjelja", "Ponedjeljak", "Utorak", "Srijeda",
        "Četvrtak", "Petak", "Subota",
      ]
      const short = ["Ned", "Pon", "Uto", "Sri", "Čet", "Pet", "Sub"]
      const narrow = ["N", "P", "U", "S", "Č", "P", "S"]
      if (width === "narrow") return narrow[n]
      if (width === "short" || width === "abbreviated") return short[n]
      return wide[n]
    },
    ordinalNumber: n => `${n}.`,
    era: n => ["pr.Kr.", "po.Kr."][n],
    quarter: n => `${n + 1}. kvartal`,
    dayPeriod: n => (n === "am" ? "prijepodne" : "poslijepodne"),
  },
  formatLong: {
    date: () => "dd.MM.yyyy",
    time: () => "HH:mm",
    dateTime: () => "dd.MM.yyyy HH:mm",
  },
  match: {},
  options: {
    weekStartsOn: 1,
    firstWeekContainsDate: 1,
  },
}

registerLocale("hr", hr)

const Prijava = ({
  data: {
    file: {
      childImageSharp: { fluid },
    },
  },
}) => {
  const [formData, setFormData] = useState({
    imeIPrezime: "",
    datumRodjenja: null,
    prebivaliste: "",
    brojTelefona: "",
    email: "",
    zanimanje: "",
    prijavaZaRadnoMjesto: "",
    prethodnoRadnoIskustvo: "",
    vozackaDozvola: "",
    napomene: "",
  })

  const [honeypot, setHoneypot] = useState("")
  const [formStartTime] = useState(Date.now())
  const [cvFile, setCvFile] = useState(null)
  const [nemamIskustvo, setNemamIskustvo] = useState(false)
  const [nemamVozacku, setNemamVozacku] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState("")
  const [emailError, setEmailError] = useState("")
  const [emailValid, setEmailValid] = useState(false)
  const [emailChecking, setEmailChecking] = useState(false)

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (name === "email") {
      setEmailError("")
      setEmailValid(false)
    }
  }

  const validateEmail = async email => {
    if (!email) return
    setEmailChecking(true)
    setEmailError("")
    try {
      const res = await fetch("/.netlify/functions/validate-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      })
      const result = await res.json()
      if (result.valid) {
        setEmailValid(true)
        setEmailError("")
      } else {
        setEmailValid(false)
        setEmailError(result.reason || "Neispravna email adresa")
      }
    } catch {
      setEmailValid(false)
      setEmailError("Greška pri provjeri email adrese")
    } finally {
      setEmailChecking(false)
    }
  }

  const handleNemamIskustvo = () => {
    setNemamIskustvo(!nemamIskustvo)
    if (!nemamIskustvo) {
      setFormData(prev => ({ ...prev, prethodnoRadnoIskustvo: "Nemam" }))
    } else {
      setFormData(prev => ({ ...prev, prethodnoRadnoIskustvo: "" }))
    }
  }

  const handleNemamVozacku = () => {
    setNemamVozacku(!nemamVozacku)
    if (!nemamVozacku) {
      setFormData(prev => ({ ...prev, vozackaDozvola: "Nemam" }))
    } else {
      setFormData(prev => ({ ...prev, vozackaDozvola: "" }))
    }
  }

  const handleFileChange = e => {
    const file = e.target.files[0]
    if (file) {
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ]
      if (!validTypes.includes(file.type)) {
        setError("Dozvoljeni formati: PDF, DOC, DOCX")
        e.target.value = ""
        setCvFile(null)
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Datoteka ne smije biti veća od 5 MB")
        e.target.value = ""
        setCvFile(null)
        return
      }
      setError("")
      setCvFile(file)
    }
  }

  const fileToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result.split(",")[1])
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!emailValid) {
      setEmailError("Molimo unesite ispravnu email adresu")
      return
    }

    setSending(true)
    setError("")

    try {
      const payload = {
        ...formData,
        _honeypot: honeypot,
        _formTime: Date.now() - formStartTime,
      }

      if (cvFile) {
        payload.cvFile = {
          name: cvFile.name,
          type: cvFile.type,
          data: await fileToBase64(cvFile),
        }
      }

      const response = await fetch("/.netlify/functions/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error("Slanje nije uspjelo")
      }

      // Save to Netlify Forms
      const netlifyData = new FormData()
      netlifyData.append("form-name", "prijava-za-posao")
      netlifyData.append("imeIPrezime", formData.imeIPrezime)
      netlifyData.append(
        "datumRodjenja",
        formData.datumRodjenja
          ? new Date(formData.datumRodjenja).toLocaleDateString("hr-HR")
          : ""
      )
      netlifyData.append("prebivaliste", formData.prebivaliste)
      netlifyData.append("brojTelefona", formData.brojTelefona)
      netlifyData.append("email", formData.email)
      netlifyData.append("zanimanje", formData.zanimanje)
      netlifyData.append("prijavaZaRadnoMjesto", formData.prijavaZaRadnoMjesto)
      netlifyData.append("prethodnoRadnoIskustvo", formData.prethodnoRadnoIskustvo)
      netlifyData.append("vozackaDozvola", formData.vozackaDozvola)
      netlifyData.append("napomene", formData.napomene)
      if (cvFile) {
        netlifyData.append("cvFile", cvFile)
      }
      await fetch("/", {
        method: "POST",
        body: netlifyData,
      }).catch(() => {
        // Don't fail the submission if Netlify Forms save fails
      })

      setSubmitted(true)
    } catch (err) {
      setError("Došlo je do greške prilikom slanja prijave. Molimo pokušajte ponovno.")
    } finally {
      setSending(false)
    }
  }

  return (
    <Layout>
      <SEO title="Prijava za posao" />
      {/* Hidden form for Netlify Forms build-time detection */}
      <form name="prijava-za-posao" data-netlify="true" encType="multipart/form-data" hidden>
        <input type="text" name="imeIPrezime" />
        <input type="text" name="datumRodjenja" />
        <input type="text" name="prebivaliste" />
        <input type="text" name="brojTelefona" />
        <input type="email" name="email" />
        <input type="text" name="zanimanje" />
        <input type="text" name="prijavaZaRadnoMjesto" />
        <textarea name="prethodnoRadnoIskustvo" />
        <input type="text" name="vozackaDozvola" />
        <textarea name="napomene" />
        <input type="file" name="cvFile" />
      </form>
      <div className="img-wrapper img-wrapper-about">
        <Image
          Tag="div"
          fluid={fluid}
          loading="eager"
          className=" bcg bcg-about  "
        />
        <h2 className="about-header visibility-filter ">Prijava za posao</h2>
        <h2 className="about-header mobile-visible">Prijava</h2>
      </div>
      <div className="about-content">
        <div className="prijava-form-wrapper">
          {submitted ? (
            <div className="prijava-success">
              <h3>Hvala na prijavi!</h3>
              <p>Vaša prijava je uspješno poslana.</p>
            </div>
          ) : (
            <>
              <h3 className="prijava-form-title">Prijava Za Posao</h3>
              <form className="prijava-form" onSubmit={handleSubmit}>
                {/* Honeypot - hidden from real users, bots will fill this */}
                <div style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }} aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={honeypot}
                    onChange={e => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                <div className="prijava-field">
                  <label htmlFor="imeIPrezime">Ime i prezime: *</label>
                  <input
                    type="text"
                    id="imeIPrezime"
                    name="imeIPrezime"
                    value={formData.imeIPrezime}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="prijava-field">
                  <label htmlFor="datumRodjenja">Datum rođenja: *</label>
                  <DatePicker
                    id="datumRodjenja"
                    selected={formData.datumRodjenja}
                    onChange={date =>
                      setFormData(prev => ({ ...prev, datumRodjenja: date }))
                    }
                    locale="hr"
                    dateFormat="dd.MM.yyyy"
                    placeholderText="dd.mm.gggg"
                    formatWeekDay={nameOfDay => nameOfDay.substr(0, 3)}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    maxDate={new Date()}
                    required
                  />
                </div>

                <div className="prijava-field">
                  <label htmlFor="prebivaliste">Prebivalište: *</label>
                  <input
                    type="text"
                    id="prebivaliste"
                    name="prebivaliste"
                    value={formData.prebivaliste}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="prijava-field">
                  <label htmlFor="brojTelefona">Broj telefona: *</label>
                  <input
                    type="tel"
                    id="brojTelefona"
                    name="brojTelefona"
                    value={formData.brojTelefona}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="prijava-field">
                  <label htmlFor="email">Email: *</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={() => validateEmail(formData.email)}
                      required
                      style={
                        emailError
                          ? { borderColor: "#ff4444" }
                          : emailValid
                          ? { borderColor: "#28a745" }
                          : {}
                      }
                    />
                    {emailChecking && (
                      <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#999", fontSize: "0.85rem" }}>
                        Provjera...
                      </span>
                    )}
                    {!emailChecking && emailValid && (
                      <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#28a745", fontSize: "1.2rem" }}>
                        ✓
                      </span>
                    )}
                  </div>
                  {emailError && (
                    <p style={{ color: "#ff4444", fontSize: "0.85rem", marginTop: "0.3rem", fontFamily: "Open Sans" }}>
                      {emailError}
                    </p>
                  )}
                </div>

                <div className="prijava-field">
                  <label htmlFor="zanimanje">Zanimanje: *</label>
                  <input
                    type="text"
                    id="zanimanje"
                    name="zanimanje"
                    value={formData.zanimanje}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="prijava-field">
                  <label htmlFor="prijavaZaRadnoMjesto">
                    Prijava za radno mjesto: *
                  </label>
                  <input
                    type="text"
                    id="prijavaZaRadnoMjesto"
                    name="prijavaZaRadnoMjesto"
                    value={formData.prijavaZaRadnoMjesto}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="prijava-field">
                  <label htmlFor="prethodnoRadnoIskustvo">
                    Prethodno radno iskustvo: *
                  </label>
                  <textarea
                    id="prethodnoRadnoIskustvo"
                    name="prethodnoRadnoIskustvo"
                    value={formData.prethodnoRadnoIskustvo}
                    onChange={handleChange}
                    disabled={nemamIskustvo}
                    required
                  />
                  <div className="prijava-nemam">
                    <input
                      type="checkbox"
                      id="nemamIskustvo"
                      checked={nemamIskustvo}
                      onChange={handleNemamIskustvo}
                    />
                    <label htmlFor="nemamIskustvo">Nemam</label>
                  </div>
                </div>

                <div className="prijava-field">
                  <label htmlFor="vozackaDozvola">Vozačka dozvola: *</label>
                  <input
                    type="text"
                    id="vozackaDozvola"
                    name="vozackaDozvola"
                    value={formData.vozackaDozvola}
                    onChange={handleChange}
                    disabled={nemamVozacku}
                    required
                  />
                  <div className="prijava-nemam">
                    <input
                      type="checkbox"
                      id="nemamVozacku"
                      checked={nemamVozacku}
                      onChange={handleNemamVozacku}
                    />
                    <label htmlFor="nemamVozacku">Nemam</label>
                  </div>
                </div>

                <div className="prijava-field">
                  <label htmlFor="napomene">Napomene:</label>
                  <textarea
                    id="napomene"
                    name="napomene"
                    value={formData.napomene}
                    onChange={handleChange}
                  />
                </div>

                <div className="prijava-field">
                  <label htmlFor="cvFile">Priložite CV (opcionalno):</label>
                  <input
                    type="file"
                    id="cvFile"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="prijava-file-input"
                  />
                </div>

                {error && (
                  <p style={{ color: "#ff4444", fontFamily: "Open Sans", fontSize: "0.95rem", textAlign: "center" }}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="prijava-submit-btn"
                  disabled={sending}
                  style={sending ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                >
                  {sending ? "Slanje..." : "Pošalji prijavu"}
                </button>
              </form>
              <div className="prijava-gdpr">
                <p>
                  <em>
                    Sukladno Uredbi (EU) 2016/679 (Opća uredba o zaštiti
                    podataka – GDPR), dajem privolu da se moji osobni podaci
                    navedeni u životopisu i ostaloj priloženoj dokumentaciji
                    koriste isključivo u svrhu provedbe postupka za
                    zapošljavanje.
                  </em>
                </p>
                <p>
                  <em>
                    Suglasan/na sam da se moji osobni podaci čuvaju i koriste u
                    svrhu budućih zapošljavanja, u razdoblju od 6 mjeseci od
                    dana podnošenja prijave za posao.
                  </em>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  )
}

export const query = graphql`
  {
    file(relativePath: { eq: "DJI_0670 (1) (1)-min (1).jpg" }) {
      childImageSharp {
        fluid(
          sizes: "(max-width: 300px) calc(100vw - 25px), (max-width: 600px) calc(100vw - 15px), 1300px"
        ) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
export default Prijava
